import { createFileRoute, redirect, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { authClient } from "@/lib/auth-client";
import { AppShell } from "@/components/layout/app-shell";
import { AppErrorBoundary } from "@/components/error-boundary";
import {
  getCachedSession,
  cacheSession,
  buildOfflineContext,
} from "@/lib/offline-session";
import { preCacheAll } from "@/lib/offline-cache";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ location }) => {
    let networkFailed = false;

    // 1. Try normal online session validation first
    try {
      const { data: session } = await authClient.getSession();
      if (session?.user) {
        // Cache the session for future offline use (fire-and-forget)
        cacheSession(session.user, session.session).catch(() => {});
        // Pre-cache read data in background (fire-and-forget)
        preCacheAll().catch(() => {});
        return {
          user: session.user,
          session: session.session,
          isOffline: false,
        };
      }
      // session is null/undefined -- user has no valid session on server.
      // If we're online, they need to log in. Don't fall through to cache.
    } catch {
      // Network error or server unreachable -- mark for offline fallback
      networkFailed = true;
    }

    // 2. If the network call failed (offline or server unreachable), try cached session.
    // This handles both navigator.onLine=false AND cases where the browser says
    // "online" but the server is unreachable (spotty mobile, server down, etc.)
    if (networkFailed) {
      const cached = await getCachedSession();
      if (cached) {
        const ctx = buildOfflineContext(cached);
        return {
          user: ctx.user,
          session: ctx.session,
          isOffline: true,
        };
      }
    }

    // 3. No valid session at all -- redirect to login
    throw redirect({
      to: "/login",
      search: { redirect: location.href },
    });
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user, isOffline } = Route.useRouteContext();

  // When the app comes back online after using a cached session,
  // silently re-validate with the server
  useEffect(() => {
    if (!isOffline) return;

    function handleOnline() {
      authClient
        .getSession()
        .then(({ data: session }) => {
          if (session?.user) {
            // Session is still valid -- update cache
            cacheSession(session.user, session.session).catch(() => {});
            // Pre-cache fresh data
            preCacheAll().catch(() => {});
          } else {
            // Session expired -- force re-login
            window.location.href = "/login";
          }
        })
        .catch(() => {
          // Server unreachable even though we're "online" -- do nothing
        });
    }

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [isOffline]);

  if (!user) return null;
  return (
    <AppShell user={user}>
      <AppErrorBoundary>
        <Outlet />
      </AppErrorBoundary>
    </AppShell>
  );
}
